import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { useDebounceFn, useMemoizedFn, useSetState } from 'ahooks';
import { isEmpty, isEqual, last } from 'lodash-es';
import { shallowEqual, useIntl, useSelector } from 'umi';
import { notification, Icon } from 'tea-component';
import { copyToClipboard } from '@/pages/aigc/utils/utils';
import {
  hasReportDocument,
  removeTags,
} from '../../ChatPage/utils';
import { useChatList } from '@/pages/aigc/layouts/agentLayout/hooks';
import { useAgentStoreContext, store } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import { useContentExporter } from '@/pages/aigc/hooks/useContentExporter';
import { useErrorTips } from '@/pages/aigc/hooks/useErrorTips';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import {
  useDocumentOperations,
  usePageLifecycle,
} from '../../ChatDSTemplate/hooks';
import {
  IReportPanelProps,
  IReportFileItem,
  TReportStepType,
  EReportModalType,
  IReportModalConfig,
} from './types';
import { EReportActions, EExportFileType, REPORT_STEP_TYPE } from './constants';

// ==================== 工具函数（纯逻辑，可复用） ====================
// 统一文本规范化逻辑：去除首尾空白，并抹平 HTML 标签与内容之间的空格差异。
// 原因：Markdown→HTML→Markdown 往返转换会导致标签前后空白不一致
// （如服务端 `<bid> {` 经编辑器 turndown 转回后变成 `<bid>{`），
// 仅做 trim() 无法消除这类差异，会导致内容对比永远不等。
const normalizeContent = (content?: string | null): string =>
  (content ?? '').trim().replace(/(<[^>]+>)\s+/g, '$1').replace(/\s+(<[^>]+>)/g, '$1');

// 判断在步骤切换场景下是否需要触发保存：
// - 仅当当前内容非空
// - 且与服务器下发的基线 htmlReport 不一致
// - 且存在有效的文档 id 时返回 true
const shouldSaveOnStepChange = (params: {
  base: string;
  current: string;
  hasDocument: boolean;
}): boolean => {
  const normalizedBase = normalizeContent(params.base);
  const normalizedCurrent = normalizeContent(params.current);

  return (
    !!normalizedCurrent &&
    normalizedCurrent !== normalizedBase &&
    params.hasDocument
  );
};

/** 内部状态类型 */
interface IActiveState {
  stepActive: TReportStepType;
  fileActive: string;
}

interface IReportValueState {
  htmlReport: string;
}

interface IShowConfigState {
  showModal: EReportModalType | undefined;
  showType: string;
}

/** Markdown 编辑器引用接口 */
interface IMarkdownEditorRef {
  getMarkdown: () => string | Promise<string>;
  handleSetHtml: () => void;
  isDirty: () => boolean;
  resetDirty: () => void;
}

/** Hook 返回类型 */
export interface IUseReportPanelReturn {
  // 状态
  stepActive: TReportStepType;
  fileActive: string;
  htmlReport: string;
  currentButton: IReportFileItem | undefined;
  reportFileList: { documents?: IReportFileItem[]; };
  loading: boolean;
  showCopyTips: boolean;
  showModal: EReportModalType | undefined;
  modalContent: IReportModalConfig | undefined;
  isDisabledReportTab: boolean;
  // Refs
  markdownRef: React.MutableRefObject<IMarkdownEditorRef | null>;
  reportLogoRef: React.RefObject<HTMLDivElement>;
  reportRef: React.RefObject<HTMLDivElement>;
  // 方法
  handleStepClick: (step: string) => void;
  handleFileItemClick: (
    item: IReportFileItem,
    closeModal: () => void
  ) => Promise<void>;
  handleAction: (action: { key: string; }) => void;
  handleExport: (type: string, isExport?: boolean) => void;
  handleCloseModal: () => void;
  setActive: (state: Partial<IActiveState>) => void;
  setReportValue: (state: Partial<IReportValueState>) => void;
}

export const useReportPanel = ({
  sessionId,
  conversations,
  dsReport,
  handleUpdateDsReport,
  startNewConversation,
}: IReportPanelProps): IUseReportPanelReturn => {
  // ==================== 外部 Hooks ====================
  const agentStore = useAgentStoreContext();
  const userInfo = useSelector(
    (state: any) => state.home.userInfo,
    shallowEqual
  );
  const { formatMessage } = useIntl();
  const { chatList, refreshAfterChat } = useChatList();
  const { exportPdf, exportTxt, exportWord } = useContentExporter();
  const showErrorTips = useErrorTips();
  // ==================== 内部状态 ====================
  const ACTIVE_CACHE_KEY = `__dba_report_active_${dsReport?.messageId}__`;
  const DEFAULT_ACTIVE: IActiveState = { stepActive: 'framework', fileActive: '' };

  // useState lazy initializer 同步决定初始 tab，避免"先渲染默认值再跳到缓存值"的闪烁
  // 处于 Chat ↔ Copilot 来回跳流程时恢复缓存，其他场景清除缓存使用默认值
  // 注：子组件 effect 早于父组件 ChatDSTemplate 的 effect 执行，此时标记尚未被清除
  const [active, setActiveRaw] = useState<IActiveState>(() => {
    const isInCopilotChatBounce =
      store.getItem('__dba_from_copilot__', false) ||
      store.getItem('__dba_to_copilot__', false);
    if (isInCopilotChatBounce) {
      return store.getItem<IActiveState>(ACTIVE_CACHE_KEY, DEFAULT_ACTIVE) || DEFAULT_ACTIVE;
    }
    store.setItem(ACTIVE_CACHE_KEY, null);
    return DEFAULT_ACTIVE;
  });
  // 自动合并 partial 更新，同时持久化到 session 缓存供跳转恢复使用
  const setActive = useMemoizedFn((update: Partial<IActiveState>) => {
    setActiveRaw((prev: IActiveState): IActiveState => {
      const next: IActiveState = {
        stepActive: update.stepActive ?? prev.stepActive,
        fileActive: update.fileActive ?? prev.fileActive,
      };
      store.setItem(ACTIVE_CACHE_KEY, next);
      return next;
    });
  });

  const [reportValue, setReportValue] = useSetState<IReportValueState>({
    htmlReport: '',
  });

  const [showConfig, setShowConfig] = useSetState<IShowConfigState>({
    showModal: undefined,
    showType: '',
  });

  const [showCopyTips, setShowCopyTips] = useState(false);
  const [lastTextMarkdown, setLastTextMarkdown] = useState('');

  // ==================== Refs ====================
  const reportRef = useRef<HTMLDivElement>(null);
  const reportLogoRef = useRef<HTMLDivElement>(null);
  const markdownRef = useRef<IMarkdownEditorRef | null>(null);

  // ==================== 解构状态 ====================
  const { stepActive, fileActive } = active;
  const { htmlReport } = reportValue;
  const { showModal, showType } = showConfig;
  const {
    document_id,
    type: dsReportType,
    messageId,
    sourceSessionId,
  } = dsReport || {};

  // ==================== 文档操作 Hook ====================
  const {
    fetchDocument,
    saveDocument,
    resetDocument,
    getFileList,
    chartsConfig,
    loading,
    listLoading,
    resetLoading,
    reportFileList,
    reportDataDetail,
  } = useDocumentOperations({
    stepActive,
    callBack: () => {
      handleUpdateDsReport({
        reportId: (last(conversations) as any)?.messageId,
      });
    },
    // 报告相关接口优先使用打开报告时绑定的会话 ID，兜底使用当前视图的 sessionId
    sessionIdForDocument: sourceSessionId || sessionId,
  });

  // ==================== 计算属性 ====================
  // 获取 markdown 内容的异步方法
  const getMarkdownAsync = useCallback(async (): Promise<string> => markdownRef.current?.getMarkdown?.() || '', []);

  // 获取当前选中的文件按钮
  const currentButton = useMemo(() => {
    const list = reportFileList?.documents;
    let button = list?.find(
      (item: IReportFileItem) => item.id + item.version === fileActive
    );

    if (isEmpty(button)) {
      button = list?.find((item: IReportFileItem) => item.id === document_id);
    }

    return button;
  }, [reportFileList, document_id, fileActive]);

  // 是否禁用分析报告 tab
  // 只有当 conversations 里已包含 report 类型文档，或 document_id 存在且类型为 report 时才放开
  const isDisabledReportTab = useMemo(
    () => !(document_id && dsReportType === 'report') && !hasReportDocument(conversations),
    [document_id, conversations, dsReportType]
  );
  // 敏感会话检测
  const sensitiveConversation = useMemo(
    () =>
      chatList.find((chat: any) => chat.session_id === sessionId)
        ?.has_sensitive_data,
    [chatList, sessionId]
  );

  // ==================== 脏标记辅助方法 ====================
  // 查询编辑器是否被用户编辑过（自上次 resetDirty 以来）
  const isEditorDirty = useCallback((): boolean => {
    return markdownRef.current?.isDirty?.() ?? false;
  }, []);

  // 重置编辑器脏标记（保存成功或文档重新加载后调用）
  const resetEditorDirty = useCallback(() => {
    markdownRef.current?.resetDirty?.();
  }, []);

  // ==================== 自动保存回调 ====================
  // 统一的自动保存入口：所有「需要保存」的场景（步骤切换、会话切换、生命周期钩子等）
  // 最终都通过该方法落盘，避免在多处散落 saveDocument 调用导致逻辑发散。
  // @param markdownOrRestInfo - 当为 string 时视为已获取的 markdown 内容（避免重复 DOM→Markdown 转换）；
  //                             当为 object 时视为额外的保存参数
  const handleAutoSaveCallback = useCallback(
    async (markdownOrRestInfo?: string | Record<string, any>) => {
      const isPreFetched = typeof markdownOrRestInfo === 'string';
      const markdown = isPreFetched ? markdownOrRestInfo : await getMarkdownAsync();
      const restInfo = isPreFetched ? undefined : markdownOrRestInfo;
      const normalizedMarkdown = normalizeContent(markdown);

      // 只有在内容非空且存在当前文档时才触发保存，避免空内容保存
      if (!normalizedMarkdown || !currentButton?.id) {
        return;
      }

      const result = saveDocument(normalizedMarkdown, {
        ...currentButton,
        document_type: stepActive,
        document_id: currentButton.id,
        cursor_position: chartsConfig,
        ...(restInfo || {}),
      });
      // 保存成功后重置脏标记，使下次轮询不会重复保存
      // 如果用户在此之后再次编辑，input 事件会重新设置脏标记为 true
      resetEditorDirty();
      return result;
    },
    [currentButton, stepActive, chartsConfig, getMarkdownAsync, saveDocument, resetEditorDirty]
  );

  // ==================== 统一的「检查 + 保存」方法 ====================
  // 将 isDirty 前置检查 + normalizeContent 内容比较 + 保存 + 更新基线的逻辑封装为一个方法，
  // 避免在 usePageLifecycle callback、trigger_report_save、handleStepClick 等多处重复编写。
  // @param options.updateBaseline - 保存成功后是否更新 htmlReport 基线（默认 false）
  const checkDirtyAndSave = useCallback(
    async (options?: { updateBaseline?: boolean; }) => {
      // 1. 脏标记前置检查：用户未编辑过则直接跳过
      if (!isEditorDirty()) return false;
      // 2. 获取当前编辑器内容
      const currentMarkdown = await getMarkdownAsync();
      // 3. 与服务器下发的 htmlReport 基线比较，normalizeContent 抹平标签前后空白差异
      const needSave = shouldSaveOnStepChange({
        base: htmlReport,
        current: currentMarkdown,
        hasDocument: !!currentButton?.id,
      });
      if (!needSave) return false;
      // 4. 执行保存（直接传入已获取的 markdown，避免再次 DOM→Markdown 转换）
      await handleAutoSaveCallback(currentMarkdown);
      // 5. 按需更新基线
      if (options?.updateBaseline) {
        setReportValue({ htmlReport: currentMarkdown });
      }
      return true;
    },
    [isEditorDirty, getMarkdownAsync, htmlReport, currentButton?.id, handleAutoSaveCallback, setReportValue]
  );

  // ==================== Effects ====================
  // 更新 markdown 内容
  useEffect(() => {
    const updateMarkdown = async () => {
      const markdown = await getMarkdownAsync();
      if (
        reportDataDetail?.cursor_position &&
        chartsConfig &&
        !isEqual(chartsConfig, reportDataDetail?.cursor_position)
      ) {
        if (markdown) {
          setLastTextMarkdown(`${markdown} `);
          return;
        }
      }
      setLastTextMarkdown(markdown);
    };

    updateMarkdown();
  }, [reportDataDetail?.cursor_position, chartsConfig, getMarkdownAsync]);

  useEffect(() => {
    setActive({
      stepActive: 'framework',
      fileActive: '',
    });
  }, [dsReport?.messageId]);

  // 页面生命周期监听
  // lastTextMarkdown 在编辑器尚未就绪时可能为空字符串，此时用 htmlReport 作为兜底基线，
  // 保证 usePageLifecycle 内的 value 始终为真值，避免 3s 自动保存条件被短路。
  // isDiffRender 让底层 hook 做第一层快速过滤（getMarkdown !== value），
  // callback 内部的 checkDirtyAndSave 做第二层精确判断（isDirty + normalizeContent）。
  usePageLifecycle({
    value: lastTextMarkdown || htmlReport,
    isDiffRender: markdownRef.current,
    callback: () => {
      void (async () => {
        if (stepActive !== 'report') {
          await checkDirtyAndSave({ updateBaseline: true });
        }
      })();
    },
  });

  // 初始化文档数据
  // 当 initType 与 dsReportType 不一致时（用户在 Chat↔Copilot 切换期间手动换过 tab），
  // document_id 属于 dsReportType 类型，无法在 initType 列表中匹配；
  // 复用 handleStepClick 逻辑：先拉列表，取第一个文件 fetch。
  useEffect(() => {
    const initDocument = async (): Promise<void> => {
      if (!document_id) return;
      // 直接从 store 读最新缓存，避免 useEffect 闭包读到旧值
      // 缓存有 fileActive 说明用户主动切换过 tab，优先使用；否则使用服务端 dsReportType
      const cachedActive = store.getItem<IActiveState | null>(ACTIVE_CACHE_KEY, null);
      const initType: TReportStepType =
        cachedActive?.fileActive
          ? cachedActive.stepActive
          : (dsReportType as TReportStepType) || 'framework';
      try {
        if (dsReportType !== initType) {
          // 类型不一致：document_id 不属于 initType，先拉 initType 列表取第一个文件
          const listRes = await getFileList({ document_type: initType });
          if (!listRes?.documents?.[0]) return;
          const { id: firstId, version: firstVersion } = listRes.documents[0];
          const docRes = await fetchDocument(firstId, firstVersion, initType);
          setReportValue({ htmlReport: docRes?.text || '' });
          setActive({ fileActive: firstId + firstVersion, stepActive: initType });
          resetEditorDirty();
          return;
        }

        // 正常初始化：document_id 与 initType 一致，直接 fetch 后核对 list 中的 version
        const res = await fetchDocument(document_id, null, initType);
        if (res?.res_code !== 0) return;
        const reportRes = await getFileList({ document_type: initType });
        if (!reportRes?.documents) return;
        const { id, version } =
          reportRes.documents.find(
            (task: IReportFileItem) => task.id === document_id
          ) || { id: '', version: '' };
        setReportValue({ htmlReport: res?.text || '' });
        setActive({ fileActive: id + version, stepActive: initType });
        resetEditorDirty();
      } catch (error) {
        console.error('Error initializing document:', error);
      }
    };
    initDocument();
  }, [dsReportType, document_id, messageId]);

  // 监听外部触发保存信号
  // 监听外部触发保存信号（如 HeaderMenu 跳转 Copilot 时）
  // 仅在报告内容相对基线实际发生变化时才触发保存，避免不必要的版本生成
  useEffect(() => {
    const unsubscribe = agentStore.subscribe('__dba_trigger_report_save__', (val: unknown) => {
      if (val) {
        agentStore.setItem('__dba_trigger_report_save__', false);
        if (currentButton?.id) {
          void checkDirtyAndSave();
        }
      }
    });
    return () => unsubscribe();
  }, [currentButton, checkDirtyAndSave]);

  // ==================== 事件处理 ====================
  // 处理文件项点击
  const handleFileItemClick = useCallback(
    async (
      item: IReportFileItem,
      closeModal: () => void
    ): Promise<void> => {
      if (item.id === fileActive) return;
      const res = await fetchDocument(item.id, item.version);
      setActive({ fileActive: item.id + item.version });
      setReportValue({
        htmlReport: res?.text,
      });
      resetEditorDirty();
      buriedBtnPoint({
        pageId: '700501',
        buttonId: '700501019',
        extInfo: JSON.stringify({
          ...item,
          message: stepActive,
        }),
        typeId: 'aigc',
      });
      closeModal();
    },
    [fileActive, stepActive, fetchDocument, setActive, setReportValue]
  );

  // 处理步骤点击
  const { run: handleStepClick } = useDebounceFn(
    async (step: string) => {
      setActive({ stepActive: step as TReportStepType });
      step !== stepActive && setShowCopyTips(false);
      try {
        // 从「分析思路」切到「分析报告」前，判断当前编辑内容是否相对 htmlReport 发生变化
        if (stepActive === REPORT_STEP_TYPE.FRAMEWORK) {
          await checkDirtyAndSave();
        }

        const res = await getFileList({ document_type: step });
        const { id: document_id, version } = res?.documents?.[0] || {};
        const reportRes = await fetchDocument(document_id, version, step);
        setReportValue({
          htmlReport: reportRes?.text,
        });
        setActive({
          fileActive: document_id + version,
        });
        buriedBtnPoint({
          pageId: '700501',
          buttonId: '700501017',
          extInfo: JSON.stringify({
            message: step,
          }),
          typeId: 'aigc',
        });
      } catch (error) {
        console.error('Error in handleStepClick:', error);
      }
    },
    {
      wait: 300,
      leading: true,
    }
  );

  // 处理内容确认（导出时使用）
  const handleContentConfirmation = useCallback(
    async (
      mutate: () => void | Promise<void>,
      showProgress: boolean
    ): Promise<void> => {
      if (!showProgress) {
        try {
          await Promise.resolve(mutate());
          notification.success({
            description: formatMessage({ id: 'dba_download_success' }),
            duration: 2000,
          });
        } catch (error) {
          showErrorTips(1005, error);
        } finally {
          setShowConfig({ showModal: undefined });
        }
        return;
      }

      const { destroy } = notification.warning({
        description: (
          <>
            <Icon type="loading" />
            {formatMessage({ id: 'dba_downloading' })}
          </>
        ),
        duration: 2000,
        className: 'dba-pdf-export-notification-loading',
      });

      try {
        await Promise.resolve(mutate());
        destroy();
        notification.success({
          description: formatMessage({ id: 'dba_download_success' }),
          duration: 2000,
        });
      } catch (error) {
        destroy();
        showErrorTips(1005, error);
      } finally {
        setShowConfig({ showModal: undefined });
      }
    },
    [formatMessage, showErrorTips, setShowConfig]
  );

  // 处理导出
  const { run: handleExport } = useDebounceFn(
    (type: string, isExport = false) => {
      const el = reportLogoRef.current as HTMLElement | null;
      if (!el) return;
      const baseName = currentButton?.display_title || currentButton?.title;
      const watermark = `${userInfo?.username || 'User'}@DataBrain`;

      const map: Record<string, () => void | Promise<void>> = {
        [EExportFileType.PDF]: () =>
          exportPdf(el, { filename: `${baseName}.pdf`, watermark }),
        [EExportFileType.TXT]: () => exportTxt(el, `${baseName}.txt`),
        [EExportFileType.Word]: () => exportWord(el, `${baseName}.doc`),
      };

      const runner = map[type];
      if (runner) {
        if (sensitiveConversation && !isExport) {
          setShowConfig({
            showModal: EReportModalType.Export,
            showType: type,
          });
          return;
        }
        handleContentConfirmation(
          runner,
          [EExportFileType.PDF].includes(type as EExportFileType)
        );
      }
    },
    {
      wait: 300,
      leading: true,
    }
  );

  // 处理重置
  const handleReset = useCallback(async () => {
    await resetDocument(currentButton?.id, stepActive, currentButton?.version)
      .then((res: any) => {
        markdownRef.current?.handleSetHtml?.();
        setReportValue({
          htmlReport: res?.text,
        });
        resetEditorDirty();
        notification.success({
          description: formatMessage({ id: 'dba_ds_report_reset_success' }),
          duration: 2000,
        });
      })
      .catch((error: any) => {
        showErrorTips(1002, error);
      })
      .finally(() => setShowConfig({ showModal: undefined }));
  }, [
    currentButton,
    stepActive,
    resetDocument,
    formatMessage,
    showErrorTips,
    setReportValue,
    setShowConfig,
  ]);

  // 处理操作
  const { run: handleAction } = useDebounceFn(
    async ({ key }: { key: string; }) => {
      const actionsHandlers: Partial<
      Record<EReportActions, () => void | Promise<void>>
      > = {
        [EReportActions.ImplementGenerate]: async () => {
          if (currentButton?.id) {
            await checkDirtyAndSave();
          }
          const { agent_model, system_language } =
            chatList?.find(
              (task: any) => task.session_id === sessionId
            ) || { agent_model: '', system_language: '' };
          startNewConversation(
            formatMessage({ id: 'dba_common_custom_q' }),
            [],
            {
              onComplete: () => {
                refreshAfterChat();
              },
            },
            {
              form: 'send',
              customQuestion: true,
              serverParams: {
                model_id: agent_model,
                system_language,
                is_auto_gen: true,
              },
              // 确保对话归属到报告所属的会话
              targetSessionId: sourceSessionId || sessionId,
              onInsertConversation: ({ conversation, targetSessionId: targetId }: { conversation: any; targetSessionId: string; }) => {
                const targetKey = `__dba_chat_history_${targetId}__`;
                const targetConversations = agentStore.getItem(targetKey, [] as any[]);
                agentStore.setItem(targetKey, [...(targetConversations || []), conversation]);
              },
            }
          );
        },
        [EReportActions.ImplementReset]: () => {
          // 当前没有有效文档时，重置无意义，直接跳过，避免发送无效请求
          if (!currentButton?.id) return;
          setShowConfig({ showModal: EReportModalType.Reset });
        },
        [EReportActions.ReportCopy]: async () => {
          const copyStatus = await copyToClipboard(removeTags(htmlReport));
          if (showCopyTips) return;
          if (copyStatus) {
            setShowCopyTips(true);
            const timer = setTimeout(() => {
              setShowCopyTips(false);
              clearTimeout(timer);
            }, 2000);
          }
        },
        [EReportActions.ReportExport]: () => {
          // 导出由 dropdown 处理
        },
        [EReportActions.ReportReset]: () => {
          // 当前没有有效文档时，重置无意义，直接跳过，避免发送无效请求
          if (!currentButton?.id) return;
          setShowConfig({ showModal: EReportModalType.Reset });
        },
        [EReportActions.ReportSave]: async () => {
          await handleAutoSaveCallback();
          const res = await getFileList({ document_type: stepActive });
          const { id: document_id, version } =
            res?.documents?.find(
              (task: IReportFileItem) => task.id === currentButton?.id
            ) || { id: '', version: '' };
          const reportRes = await fetchDocument(document_id, version);
          setReportValue({
            htmlReport: reportRes?.text,
          });
          setActive({
            fileActive: document_id + version,
          });
          resetEditorDirty();
        },
      };

      const handler = actionsHandlers[key as EReportActions];
      if (handler) {
        await handler();
      }
      buriedBtnPoint({
        pageId: '700501',
        buttonId: '700501018',
        extInfo: JSON.stringify({
          message: key,
          ...currentButton,
        }),
        typeId: 'aigc',
      });
    },
    {
      wait: 300,
      leading: true,
    }
  );

  // 弹窗内容
  const modalContent: IReportModalConfig | undefined = useMemo(() => {
    switch (showModal) {
      case EReportModalType.Export:
        return {
          caption: formatMessage({ id: 'dba_ds_report_export' }),
          bodyDesc: formatMessage({ id: 'dba_ds_report_export_content' }),
          showCancel: false,
          onClick: () => handleExport(showType, true),
        };
      case EReportModalType.Reset:
        return {
          caption: formatMessage({ id: 'dba_ds_report_reset' }),
          bodyDesc: formatMessage({ id: 'dba_ds_report_reset_content' }),
          showCancel: true,
          onClick: handleReset,
        };
      default:
        return undefined;
    }
  }, [showModal, showType, formatMessage, handleExport, handleReset]);

  // 关闭弹窗
  const handleCloseModal = useCallback(() => {
    setShowConfig({ showModal: undefined });
  }, [setShowConfig]);

  // ==================== 返回 ====================
  return {
    // 状态
    stepActive,
    fileActive,
    htmlReport,
    currentButton,
    reportFileList,
    loading: listLoading || loading || resetLoading,
    showCopyTips,
    showModal,
    modalContent,
    isDisabledReportTab,
    // Refs
    markdownRef,
    reportLogoRef,
    reportRef,
    // 方法
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleStepClick,
    handleFileItemClick,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleAction,
    handleExport,
    handleCloseModal,
    setActive,
    setReportValue,
  };
};

export default useReportPanel;
